import { DocumentSignEventResponse } from '@/lib/api';
import { LedgerBadge } from './LedgerBadge';

interface AuditTrailProps {
  events: DocumentSignEventResponse[];
}

const TYPE_LABELS: Record<string, string> = {
  EnvelopeCreated: 'Envelope created',
  EnvelopeSent: 'Sent for signature',
  RecipientNotified: 'Recipient notified',
  RecipientViewed: 'Recipient viewed',
  ConsentAccepted: 'Consent accepted',
  RecipientSigned: 'Recipient signed',
  RecipientDeclined: 'Recipient declined',
  RecipientDeclinedElectronic: 'Recipient requested a paper copy',
  EnvelopeCompleted: 'Envelope completed',
  EnvelopeDeclined: 'Envelope declined',
  EnvelopeVoided: 'Envelope voided',
  EnvelopeExpired: 'Envelope expired',
};

const ANCHORED_TYPES = new Set([
  'EnvelopeCreated',
  'EnvelopeSent',
  'ConsentAccepted',
  'RecipientSigned',
  'RecipientDeclined',
  'RecipientDeclinedElectronic',
  'EnvelopeCompleted',
  'EnvelopeVoided',
]);

/**
 * Vertical timeline of ds_events with per-event ledger badges. Renders each
 * event's type, actor, timestamp, IP, and ledger state. Anchored events
 * always carry a badge; non-anchored events get a muted "off-chain" pill so
 * a future operator can tell at a glance what is and isn't on the chain.
 */
export function AuditTrail({ events }: AuditTrailProps) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">No events yet.</p>;
  }
  return (
    <ol className="space-y-3 text-sm">
      {events.map((ev) => (
        <li
          key={ev.eventId}
          className="border-l-2 border-muted pl-3 -ml-px py-1 flex flex-col gap-1"
          data-event-type={ev.type}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{TYPE_LABELS[ev.type] ?? ev.type}</span>
            {ANCHORED_TYPES.has(ev.type) || ev.ledgerStatus !== 'disabled' ? (
              <LedgerBadge status={ev.ledgerStatus} ledgerRef={ev.ledgerRef} />
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
            <time dateTime={ev.createdAt}>{new Date(ev.createdAt).toLocaleString()}</time>
            <span>actor: {ev.actorKind}</span>
            {ev.ip && <span>ip: {ev.ip}</span>}
            {ev.ledgerLastError && (
              <span className="text-destructive">err: {truncate(ev.ledgerLastError, 60)}</span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
